import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ClockInConfirm from './ClockInConfirm';

// Edit Employee → Attendance Settings → "Force clock-in": such an employee must clock in
// before using Kanban. Clock-in status is shared with the admin app, HRMS and the PWA
// (one clock-in counts everywhere). Inside the admin app (iframe) the admin app shows the gate.
const baseUrl = String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const embedded = typeof window !== 'undefined' && window.self !== window.top;

const cleanToken = (t) => {
  try {
    return t && t.startsWith('"') ? JSON.parse(t) : t;
  } catch (e) {
    return t;
  }
};

const isSuperAdmin = () => {
  try {
    return (JSON.parse(localStorage.getItem('userData') || '{}')?.role || '').toLowerCase() === 'superadmin';
  } catch (e) {
    return false;
  }
};

const call = async (path, body) => {
  const token = cleanToken(localStorage.getItem('access_token'));
  const res = await fetch(`${baseUrl}${path}`, {
    method: body ? 'POST' : 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'company-id': cleanToken(localStorage.getItem('company-id')) || ''
    },
    body: body ? JSON.stringify(body) : undefined
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data?.message || 'Could not reach the server. Try again.'), { code: data?.detail?.code || data?.code });
  return data?.data || {};
};

const timeOf = (d) => (d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
// "08:34" today, "yesterday 08:34", else "Mon 27 Oct, 08:34" — a time alone looked the same every day
const sinceText = (d) => {
  if (!d) return '';
  const at = new Date(d);
  const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(at).setHours(0, 0, 0, 0)) / 86400000);
  if (days === 0) return timeOf(at);
  if (days === 1) return `yesterday ${timeOf(at)}`;
  return `${at.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}, ${timeOf(at)}`;
};
// time worked so far: "45 min", "3 h 05 min"
const durationText = (d) => {
  const min = Math.max(0, Math.floor((Date.now() - new Date(d)) / 60000));
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')} min`;
};
const REFRESH_MS = 60000; // re-read the real status (clock-out at the kiosk, forgotten clock-out)

export default function ForceClockInGate() {
  const [state, setState] = useState(null); // { forceClockIn, clockedIn, since }
  const busy = false;
  const error = '';
  const [askOut, setAskOut] = useState(false);
  const [confirming, setConfirming] = useState(false); // false | true (clock in) | 'out' — face / PIN check

  const refresh = useCallback(async () => {
    if (embedded || isSuperAdmin() || !localStorage.getItem('access_token')) return;
    try {
      setState(await call('/attendanceSettings/me'));
    } catch (e) {
      // never lock someone out because the check failed
    }
  }, []);

  useEffect(() => {
    refresh();
    const onVisible = () => document.visibilityState === 'visible' && refresh();
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(() => document.visibilityState === 'visible' && refresh(), REFRESH_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [refresh]);

  const logout = () => {
    const token = cleanToken(localStorage.getItem('access_token'));
    if (token) {
      fetch(`${baseUrl}/auth/logout`, {
        method: 'POST',
        keepalive: true,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: 'user' })
      }).catch(() => {});
    }
    localStorage.clear();
    window.location.replace('/login');
  };

  if (embedded || !state || !state.forceClockIn) return null;

  if (state.clockedIn) {
    return (
      <>
        <div style={state.forgotClockOut ? { ...styles.pill, ...styles.pillWarn } : styles.pill} data-no-draft="1">
          <span title={state.since ? new Date(state.since).toLocaleString() : ''}>
            {state.forgotClockOut && <b>Did you forget to clock out? </b>}
            Clocked in since {sinceText(state.since)} · {durationText(state.since)}
          </span>
          <button type="button" style={styles.pillBtn} disabled={busy} onClick={() => setAskOut(true)}>
            Clock out
          </button>
        </div>
        {askOut && (
          <div style={styles.backdrop} onClick={(e) => e.target === e.currentTarget && !busy && setAskOut(false)}>
            <div role="dialog" aria-modal="true" aria-labelledby="fci-out-title" style={styles.box}>
              <h5 id="fci-out-title" style={styles.title}>
                Clock out now?
              </h5>
              <p style={styles.text}>You will need to clock in again to keep using the system.</p>
              <div style={styles.row}>
                <button type="button" style={styles.secondary} onClick={() => setAskOut(false)} disabled={busy}>
                  Cancel
                </button>
                <button
                  type="button"
                  style={styles.primary}
                  autoFocus
                  onClick={() => {
                    // clocking out also needs the face / PIN check
                    setAskOut(false);
                    setConfirming('out');
                  }}
                >
                  Clock out
                </button>
              </div>
            </div>
          </div>
        )}
        {confirming === 'out' && (
          <div style={styles.backdrop}>
            <div role="dialog" aria-modal="true" style={{ ...styles.box, maxWidth: 460 }}>
              <ClockInConfirm
                action="out"
                api={call}
                onCancel={() => setConfirming(false)}
                onDone={(d) => {
                  setConfirming(false);
                  setState((s) => ({ forceClockIn: s.forceClockIn, clockedIn: !!d.clockedIn, since: d.since }));
                  toast.success(`Clocked out at ${timeOf(d.time)}`);
                }}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  if (confirming) {
    return (
      <div style={styles.backdrop}>
        <div role="dialog" aria-modal="true" style={{ ...styles.box, maxWidth: 460 }}>
          <ClockInConfirm
            api={call}
            onCancel={() => setConfirming(false)}
            onDone={(d) => {
              setConfirming(false);
              setState((s) => ({ forceClockIn: s.forceClockIn, clockedIn: !!d.clockedIn, since: d.since }));
              toast.success(`Clocked in at ${timeOf(d.time)}`);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.backdrop}>
      <div role="dialog" aria-modal="true" aria-labelledby="fci-title" style={styles.box}>
        <h5 id="fci-title" style={styles.title}>
          Clock in to start
        </h5>
        <p style={styles.text}>Your company asks you to clock in before using the system. It is {timeOf(new Date())}.</p>
        {error && (
          <p role="alert" style={{ color: '#b91c1c', margin: '0 0 12px' }}>
            {error}
          </p>
        )}
        <div style={styles.row}>
          <button type="button" style={styles.secondary} onClick={logout} disabled={busy}>
            Log out
          </button>
          <button type="button" style={styles.primary} onClick={() => setConfirming(true)} disabled={busy} autoFocus>
            {busy ? 'Clocking in…' : 'Clock in'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.7)',
    zIndex: 19000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16
  },
  box: {
    background: '#fff',
    borderRadius: 10,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    textAlign: 'center',
    boxShadow: '0 12px 40px rgba(0,0,0,0.25)'
  },
  title: { margin: '0 0 8px', fontWeight: 600, fontSize: 18 },
  text: { margin: '0 0 16px', color: '#4b5563' },
  row: { display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' },
  primary: { border: 0, borderRadius: 8, padding: '8px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: '#1a7d3e', color: '#fff' },
  secondary: { border: 0, borderRadius: 8, padding: '8px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: '#eef2f6', color: '#1f2937' },
  pill: {
    position: 'fixed',
    left: 16,
    bottom: 16,
    zIndex: 1500,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 999,
    padding: '6px 6px 6px 14px',
    fontSize: 13,
    boxShadow: '0 4px 14px rgba(0,0,0,0.12)'
  },
  // open longer than 16 h: probably forgot to clock out
  pillWarn: { background: "#fff7ed", border: "1px solid #fb923c", color: "#9a3412" },
  pillBtn: { color: '#1f2937', border: 0, borderRadius: 999, padding: '6px 12px', background: '#eef2f6', fontWeight: 600, fontSize: 12, cursor: 'pointer' }
};
